// themeSettings.js — the colours of the software, set per company.
//
// Company-wide, stored as RestSetting "ThemeSettings" through the same
// businesssettings endpoint as Sound Settings and KOT Settings. No new table,
// no SQL.
//
// ── Why CSS variables and not just the MUI theme ────────────────────────────
//
// There IS a root MUI theme (admin/theme.js) and it feeds one ThemeProvider.
// Almost nothing uses it: a sweep of src found 4,909 hard-coded hex colours
// across 159 files and only 18 references to the theme palette. So changing
// theme.js alone repaints almost nothing — every button carries its own
// '#1976d2' inline.
//
// A CSS custom property set on :root reaches BOTH. MUI components read the
// theme (which is wired to the same variables), and a hand-written
// sx={{ bgcolor: 'var(--hd-btn-bg)' }} reads it directly. That is what makes
// it possible to convert the app screen by screen instead of in one
// irreversible rewrite: a file that has been converted follows the setting,
// a file that has not keeps its old colour and nothing breaks either way.
//
// ── The tokens ──────────────────────────────────────────────────────────────
//
// 429 distinct colours are in use. They are NOT 429 settings — they fall into
// a handful of jobs, and these are those jobs. Every colour in the app maps
// onto one of them.
import config from '../../config';

export const THEME_SETTINGS_KEY = 'ThemeSettings';
export const THEME_SETTINGS_EVENT = 'themeSettingsChanged';

// The defaults ARE today's colours, read off the app as it stands. A company
// that never opens this page sees no change whatsoever.
export const DEFAULT_THEME = {
  // Action buttons — the thing people asked for first.
  btnBg: '#1976d2',
  btnText: '#ffffff',
  btnHover: '#1565c0',

  // Blue TEXT, icons, spinners and accents. The same blue as the button
  // today, but a separate setting on purpose: a heading, an icon and a
  // progress ring sit on a white page, while the button colour sits behind
  // white text. Tie them together and a company that picks a pale button
  // gets headings nobody can read.
  link: '#1976d2',

  // Dark table headers / section bars.
  headerBg: '#0a2540',
  headerText: '#ffffff',

  // Surfaces.
  pageBg: '#f8fafc',
  surface: '#ffffff',
  border: '#e2e8f0',

  // Text.
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',

  // Status.
  success: '#16a34a',
  warning: '#b45309',
  danger: '#dc2626',
  info: '#2563eb',

  // Brand accent (selected menu, badges, highlights).
  accent: '#c8102e',
};

// token -> CSS variable. One place, so a token added here needs no edit
// anywhere else.
export const CSS_VAR = Object.keys(DEFAULT_THEME).reduce((m, k) => {
  // btnBg -> --hd-btn-bg
  m[k] = '--hd-' + k.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
  return m;
}, {});

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/// Anything that is not a plain hex colour is dropped, not "fixed". A stored
/// value reaches this from the database and ends up inside a style attribute,
/// so a key nobody recognises or a value that is not a colour must never be
/// written into the page.
export function mergeTheme(json) {
  let saved = {};
  if (json) {
    try {
      saved = typeof json === 'string' ? JSON.parse(json) : json;
    } catch {
      saved = {};
    }
  }
  const out = { ...DEFAULT_THEME };
  for (const key of Object.keys(DEFAULT_THEME)) {
    const v = saved?.[key];
    if (typeof v === 'string' && HEX.test(v.trim())) out[key] = v.trim().toLowerCase();
  }
  return out;
}

/// Writes the colours onto :root. Everything converted to var(--hd-*) follows
/// on the next paint — no reload, no re-render, no React state.
export function applyTheme(settings) {
  const clean = mergeTheme(settings);
  try {
    const root = document.documentElement;
    for (const [key, value] of Object.entries(clean)) {
      root.style.setProperty(CSS_VAR[key], value);
    }
  } catch {
    /* no DOM (tests, SSR) - the defaults in the stylesheet still apply */
  }
  return clean;
}

/// Puts the colours back to the shipped ones WITHOUT saving — used by the
/// settings page's preview when the user presses Reset.
export function clearTheme() {
  return applyTheme(DEFAULT_THEME);
}

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`,
});

// Remembered so a reload paints the company's colours IMMEDIATELY, before the
// settings have been fetched. Without it every page flashes the default blue
// for as long as the request takes. Per browser, so it is only ever a cache -
// the database stays the truth.
const CACHE_KEY = 'hdThemeSettings';

export function applyCachedTheme() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return applyTheme(JSON.parse(raw));
  } catch {
    /* private window, cleared storage - fall through to the defaults */
  }
  return applyTheme(DEFAULT_THEME);
}

function cache(clean) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(clean));
  } catch {
    /* storage blocked - the colours still apply for this page */
  }
}

export async function fetchThemeSettings(companyId) {
  const res = await fetch(
    `${config.API_BASE_URL}/businesssettings/${companyId}/${THEME_SETTINGS_KEY}`,
    { headers: authHeaders() },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const d = await res.json();
  return mergeTheme(d?.json);
}

/// Fetches AND applies. This is what the app calls on load; a failure leaves
/// the cached (or default) colours alone rather than blanking the screen.
export async function loadAndApplyTheme(companyId) {
  if (!companyId) return applyCachedTheme();
  try {
    const clean = await fetchThemeSettings(companyId);
    cache(clean);
    return applyTheme(clean);
  } catch {
    return applyCachedTheme();
  }
}

export async function saveThemeSettings(companyId, settings) {
  const clean = mergeTheme(settings);
  const res = await fetch(`${config.API_BASE_URL}/businesssettings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      CompanyId: companyId,
      Key: THEME_SETTINGS_KEY,
      Json: JSON.stringify(clean),
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  cache(clean);
  applyTheme(clean);
  // Other open tabs/screens of this company repaint too.
  try {
    window.dispatchEvent(new CustomEvent(THEME_SETTINGS_EVENT, { detail: clean }));
  } catch {
    /* nothing listening */
  }
  return clean;
}

// ── For the converted files ─────────────────────────────────────────────────
//
// The ONE action-button style, replacing the 25 copies of ACTION_BTN_SX / BTN
// that each file had grown its own version of. Imported, never re-declared -
// a 26th copy is how the next colour setting gets missed.
/// `v('btnBg')` -> "var(--hd-btn-bg, #1976d2)". The fallback is the token's
/// default, so a page that renders before the variables are set (login, the
/// guest's QR menu, a print preview) paints today's colour, never nothing.
/// Defined ABOVE the objects below: they are built at module load and would
/// hit the temporal dead zone if this came after them.
export const v = (token) => `var(${CSS_VAR[token]}, ${DEFAULT_THEME[token]})`;

export const ACTION_BTN = {
  backgroundImage: 'none',
  bgcolor: v('btnBg'),
  color: v('btnText'),
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundImage: 'none',
    bgcolor: v('btnHover'),
  },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    // The button's own colour, faded - so a disabled button still reads as
    // this company's button and not as a grey one from another product.
    // Plain opacity, not color-mix(): color-mix is recent, and where it is not
    // understood the whole declaration is dropped and the button loses its
    // background entirely rather than merely looking wrong.
    bgcolor: v('btnBg'),
    color: v('btnText'),
    opacity: 0.45,
  },
};

/// Dark table head, as used by every list screen.
export const TABLE_HEAD = {
  fontWeight: 700,
  bgcolor: v('headerBg'),
  color: v('headerText'),
  whiteSpace: 'nowrap',
};

// v() lives above ACTION_BTN - see the note there.
